import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Radio,
  Stack,
  Divider,
  FormControl,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import { Question } from "./type";
import { RenderHTMLContent, RenderHTMLContent2 } from "./extractQuestions";
import { green, pink } from "@mui/material/colors";
import ImageBox from "./ImageBox";
import PillButton from "./PillButton";

// Props for the QuestionPart component
interface QuestionPartProps {
  partLabel: string; // 'a', 'b', 'c', 'd'
  questionContent: string;
  value: string | null;
  onChange: (newValue: string | null) => void;
  formatSize?: number;
  questionImageUrl?: string;
}

const QuestionPart: React.FC<QuestionPartProps> = ({
  partLabel,
  questionContent,
  questionImageUrl,
  value,
  onChange,
  formatSize = 1,
}) => {
  return (
    <Box
      mb={0.5}
      display="flex"
      alignItems="center"
      justifyContent="space-between"
    >
      <Box
        sx={{
          position: "relative",
          alignItems: "center",
          display: "flex",
        }}
      >
        <Typography variant="examText">
          {/* <strong>{partLabel})</strong> {RenderHTMLContent2(questionContent)} */}
          {RenderHTMLContent2(questionContent, partLabel + ")")}
        </Typography>
        {questionImageUrl && (
          // <Box
          //   component="img"
          //   src={questionImageUrl}
          //   alt={`Question ${questionImageUrl}`}
          //   onLoad={handleImageLoad}
          //   sx={{
          //     float: 'right',
          //     marginLeft: 2,
          //     marginBottom: 2,
          //     width: imageSize.width * formatSize + 'px',
          //     height: 'auto',
          //   }}
          // />
          <ImageBox
            src={questionImageUrl}
            alt={`Question ${questionImageUrl}`}
            formatSize={formatSize}
          />
        )}
      </Box>

      <FormControl
        sx={{
          minWidth: "auto",
        }}
      >
        <RadioGroup
          row
          aria-label={`radio-${partLabel}`}
          name={`radio-${partLabel}`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          sx={{ display: "flex", flexDirection: "row", flexWrap: "nowrap" }}
        >
          <FormControlLabel
            value="1"
            control={
              <Radio
                sx={{
                  "& .MuiSvgIcon-root": {
                    fontSize: formatSize + 0.5 + "rem",
                  },
                  color: green[800],
                  "&.Mui-checked": {
                    color: green[600],
                  },
                  p: 0,
                }}
              />
            }
            label={<></>}
            sx={{ py: formatSize - 0.5, mx: 0 }}
            labelPlacement="start"
          />
          <FormControlLabel
            value="0"
            control={
              <Radio
                sx={{
                  "& .MuiSvgIcon-root": {
                    fontSize: formatSize + 0.5 + "rem",
                  },
                  color: pink[800],
                  "&.Mui-checked": {
                    color: pink[600],
                  },
                  pr: 0,
                }}
              />
            }
            label={<></>}
            sx={{ py: formatSize - 0.5, mx: 0 }}
            labelPlacement="start"
          />
        </RadioGroup>
      </FormControl>

      {/* <Stack direction="row">
        <Radio
          checked={value === '1'}
          onChange={() => onChange('1')}
          value="1"
          name={`radio-${partLabel}`}
          inputProps={{ 'aria-label': '1' }}
          
          sx={{
            '& .MuiSvgIcon-root': {
              fontSize: formatSize + 0.5 + 'rem',
            },
          }}
        />
        <Radio
          checked={value === '0'}
          onChange={() => onChange('0')}
          value="0"
          name={`radio-${partLabel}`}
          inputProps={{ 'aria-label': '0' }}
          sx={{
            '& .MuiSvgIcon-root': {
              fontSize: formatSize + 0.5 + 'rem',
            },
          }}
        />
      </Stack> */}
    </Box>
  );
};

interface MCTFRowProps {
  label: "A" | "B" | "C" | "D";
  value: "1" | "0" | null;
  disabled?: boolean;
  onChange: (v: "1" | "0") => void;
  checkCorrect?: boolean;
  answer?: string;
}

const MCTFRow: React.FC<MCTFRowProps> = ({
  label,
  value,
  disabled,
  onChange,
  checkCorrect = false,
  answer,
}) => {
  return (
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Typography width={20}> {label.toLowerCase() + ")"}</Typography>

      <Box display="flex" gap={1}>
        <PillButton
          label="Đ"
          selected={value === "1"}
          disabled={disabled}
          onClick={() => onChange("1")}
          isCheckAnswer={checkCorrect}
          isCorrectAnswer={answer === "1"}
        />
        <PillButton
          label="S"
          selected={value === "0"}
          disabled={disabled}
          onClick={() => onChange("0")}
          isCheckAnswer={checkCorrect}
          isCorrectAnswer={answer === "0"}
        />
      </Box>
    </Box>
  );
};

interface MCTFQuestionProps {
  question: Question;
  editable?: boolean;
  onAnswerChange?: (answer: string) => void;
  answer?: string;
  formatSize?: number;
  checkCorrect?: boolean;
}

const MCTFQuestion: React.FC<MCTFQuestionProps> = ({
  question,
  editable = true,
  onAnswerChange,
  answer,
  checkCorrect = false,
}) => {
  const [answers, setAnswers] = useState<{
    A: "1" | "0" | null;
    B: "1" | "0" | null;
    C: "1" | "0" | null;
    D: "1" | "0" | null;
  }>({
    A: null,
    B: null,
    C: null,
    D: null,
  });

  useEffect(() => {
    const src = answer ?? question.correctAnswer ?? "____";
    setAnswers({
      A: src[0] === "1" || src[0] === "0" ? (src[0] as any) : null,
      B: src[1] === "1" || src[1] === "0" ? (src[1] as any) : null,
      C: src[2] === "1" || src[2] === "0" ? (src[2] as any) : null,
      D: src[3] === "1" || src[3] === "0" ? (src[3] as any) : null,
    });
  }, [answer, question.correctAnswer]);

  const handleChange = (key: "A" | "B" | "C" | "D", v: "1" | "0") => {
    if (!editable || checkCorrect) return;

    const next = { ...answers, [key]: v };
    setAnswers(next);

    onAnswerChange?.(
      `${next.A ?? "_"}${next.B ?? "_"}${next.C ?? "_"}${next.D ?? "_"}`,
    );
  };

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      {(["A", "B", "C", "D"] as const).map((k) => (
        <MCTFRow
          key={k}
          label={k}
          value={answers[k]}
          disabled={!editable || checkCorrect}
          onChange={(v) => handleChange(k, v)}
          checkCorrect={checkCorrect}
          answer={question.correctAnswer[k.charCodeAt(0) - 65]}
        />
      ))}
    </Box>
  );
};

export default MCTFQuestion;
